import _ from 'lodash';
import React, { Fragment, useState, useEffect } from 'react';
import cn from 'classnames';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { setBackUrl } from 'shared/lib/backUrl';
import {
  createMoneyFormatter,
  createMoneyRangeConvertor,
  formatDateTime,
  formatRateRange
} from 'shared/lib/formatters';
import { calcMonthlyRate } from 'shared/lib/monthlyRates';
import {
  DEFAULT_CURRENCY,
  EMPLOYMENT,
  RATE_PERIOD,
  RATE_TYPE,
  MATCH
} from 'shared/constants';
import { userportal as routes, createRoute, createUserportalLink, createStaticsLink } from 'shared/routes';
import createMatcher from 'shared/lib/matcher';
import { buildSeoTitle, buildMetaDescription } from 'shared/lib/seoHelpers';
import featureFlags from 'shared/lib/featureFlags';
import createSchemaOrg from 'userportal/lib/schemaOrg';
import Loadable from 'shared/components/Loadable';
import Subheader from 'shared/components/Subheader';
import Card, { CardList } from 'shared/components/Card';
import WysiwygContent from 'shared/components/WysiwygContent';
import Score from 'shared/components/Score';
import ScoreDims from 'shared/components/Score/Dims';
import LevelsHint from 'shared/components/LevelsHint';
import Tag, { TagList } from 'shared/components/Tag';
import Tip from 'shared/components/Tip';
import Hint from 'shared/components/Hint';
import Collapsable from 'shared/components/Collapsable';
import JobAttribute from 'shared/components/JobAttribute';
import ButtonWithModal from 'shared/components/ButtonWithModal';
import { Submit, Button } from 'shared/components/Form';
import Tooltip from 'shared/components/Tooltip';
import apiClient from 'userportal/lib/ApiClient';
import { getPathname } from 'shared/lib/jobSearchQuery';
import Page from 'userportal/components/Page';
import JobPreview from 'userportal/components/Job/Preview';
import NotFound from 'userportal/components/NotFound';
import JobHeader from 'userportal/components/Job/Header';
import JobApplicationForm from 'userportal/components/Job/Application/Form';
import { selectors as authSelectors } from 'userportal/ducks/auth';
import { selectors as profileSelectors } from 'userportal/ducks/profile';
import { actions as visitedActions } from 'userportal/ducks/visited';
import { selectors as favoriteJobsSelectors } from 'userportal/ducks/favoriteJobs';
import { selectors as offerSelectors } from 'userportal/ducks/offers';
import { selectors as currenciesSelectors } from 'userportal/ducks/currencies';
import googleAnalytics from 'userportal/lib/googleAnalytics';
import {
  UserJobReviewModal,
  JobApplicationButton,
  EduPromos,
  AccountRequiredModal,
} from './components';
import styles from './JobDetail.module.scss';

const MATCH_GROUPS = [MATCH.FULL, MATCH.PARTIAL, MATCH.LOW];

export const JobDetail = () => {
  const { t, i18n } = useTranslation('userportal');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug: jobSlug } = useParams();

  const loggedIn = useSelector(authSelectors.isLoggedIn);
  const currency = useSelector(authSelectors.getUserCurrency) || DEFAULT_CURRENCY;
  const currencies = useSelector(currenciesSelectors.getCurrencies);
  const hasInsufficientProfile = useSelector(profileSelectors.hasInsufficientProfile);
  const profile = useSelector(profileSelectors.getUserProfile);
  const profileRoles = useSelector(profileSelectors.getRolesForMatching);
  const profileSkills = useSelector(profileSelectors.getSkillsForMatching);
  const profileLanguages = useSelector(profileSelectors.getLanguagesForMatching);
  const profileIgnoredSkills = useSelector(profileSelectors.getIgnoredSkillsMap);

  const searchId = useSelector(state => state.jobSearch.id);
  const user = useSelector(authSelectors.getUser);
  const userId = user ? user.id : null;

  const convertMoneyRange = createMoneyRangeConvertor(t, i18n.language, currencies);

  const [state, setState] = useState({ job: null, loading: false, error: null, errorCode: null });
  const [applicationFormOpened, setApplicationFormOpened] = useState(false);
  const [accountRequiredModalOpened, setAccountRequiredModalOpened] = useState(false);
  const [similarJobs, setSimilarJobs] = useState([]);

  const toggleApplicationFormOpened = () => {
    setApplicationFormOpened(prev => !prev);
  };

  const toggleAccountRequiredModal = () => {
    if (!accountRequiredModalOpened) {
      googleAnalytics.event('account_required_modal_open', {
        slug: state.job ? state.job.slug : undefined,
      });
    }
    setAccountRequiredModalOpened(prev => !prev);
  };

  const { job } = state;
  const activeOffer = useSelector(_state => offerSelectors.getActiveOfferByJobId(_state, job ? job.id : null));

  const loadJob = async () => {
    setState(prev => ({ ...prev, loading: true }));
    let jobDetail;
    try {
      jobDetail = await apiClient.get(`/jobs/${jobSlug}`, {}, { searchId });
      setState({ loading: false, error: null, job: jobDetail });
    } catch (err) {
      setState({
        loading: false,
        error: err.status !== 404 ? err.error : null,
        errorCode: err.status,
        job: {}
      });
    }

    if (jobDetail && jobDetail.id) {
      try {
        const similars = await apiClient.get(`/jobs/${jobDetail.id}/similar`, {});
        setSimilarJobs(similars);
      } catch (err) {
        Sentry.captureException(err);
      }
    }
  };

  useEffect(() => {
    if (job) {
      loadJob();
    }
  }, [i18n.language]);

  useEffect(() => {
    loadJob();
  }, [jobSlug]);

  useEffect(() => {
    if (loggedIn && job && job.id) {
      dispatch(visitedActions.setVisited(job.id));
    }

    if (job && job.redirection) {
      navigate(createRoute(routes.job.detail, { slug: job.slug }), { replace: true });
    }
  }, [job]);

  const matcher = createMatcher(loggedIn, profileRoles, profileSkills, profileLanguages);

  const expectedMonthlyRate = loggedIn && profile && ~~profile.rate > 0
    ? calcMonthlyRate(profile.rate, profile.ratePeriod)
    : 0;

  const renderRateExpectation = (employment) => {
    if (!expectedMonthlyRate || employment.rateType === RATE_TYPE.UNPAID) {
      return null;
    }

    const offered = employment.hideRateUpTo
      ? employment.monthlyRate
      : (employment.monthlyRateUpTo || employment.monthlyRate);

    const profileCurrency = profile.currency || DEFAULT_CURRENCY;
    const exchangeRate = currencies[employment.currency] && currencies[employment.currency][profileCurrency];

    if (!offered || !exchangeRate) {
      return null;
    }

    const percent = Math.round((((offered * exchangeRate) - expectedMonthlyRate) / expectedMonthlyRate) * 100);
    // with a hidden upper rate bound the real offer may be higher, so require
    // a bigger difference before warning that the rate is below expectation
    const belowThreshold = employment.hideRateUpTo ? -10 : -5;
    if ((percent > belowThreshold && percent < 5) || Math.abs(percent) > 199) {
      return null;
    }

    const formatMoney = createMoneyFormatter(i18n.language);

    const isRange = !employment.hideRateUpTo && employment.monthlyRateUpTo && employment.monthlyRateUpTo !== employment.monthlyRate;
    let hintKey = 'rateExpectationHint';
    if (employment.hideRateUpTo) {
      hintKey = 'rateExpectationFromHint';
    } else if (isRange) {
      hintKey = percent > 0 ? 'rateExpectationUpToHint' : 'rateExpectationMaxHint';
    }

    return (
      <span className={styles.rateExpectationRow}>
        <span className={cn(styles.rateExpectation, percent > 0 ? styles.aboveExpectation : styles.belowExpectation)}>
          {t(`common:job.${percent > 0 ? 'rateAboveExpectation' : 'rateBelowExpectation'}`, { percent: Math.abs(percent) })}
          <Hint
            iconClassName="la la-info-circle"
            tooltip={t(`common:job.${hintKey}`, {
              expected: formatMoney(expectedMonthlyRate, profileCurrency),
              offered: formatMoney(Math.round(offered * exchangeRate), profileCurrency)
            })}
            tooltipClassName={styles.hiddenRateTooltip}
          />
        </span>
      </span>
    );
  };

  const formatRate = (companyName, employment) => {
    if (employment.rateType === RATE_TYPE.UNPAID) {
      return t(`common:rateType.${RATE_TYPE.UNPAID}`);
    }

    if (employment.ratePeriod === RATE_PERIOD.MONTHLY) {
      return (
        <>
          {convertMoneyRange(employment.rate, employment.rateUpTo, employment.currency, currency, employment.hideRateUpTo)}
          {' '}
          {t(`common:ratePeriod.${employment.ratePeriod}`)}
          {employment.hideRateUpTo && (
            <Hint iconClassName="la la-info-circle" tooltip={t('common:job.rateFromHint', { companyName })} tooltipClassName={styles.hiddenRateTooltip} />
          )}
          {renderRateExpectation(employment)}
        </>
      );
    }

    return (
      <>
        {convertMoneyRange(employment.monthlyRate, employment.monthlyRateUpTo, employment.currency, currency, employment.hideRateUpTo)}
        {' '}
        {t(`common:ratePeriod.${RATE_PERIOD.MONTHLY}`)}
        {employment.hideRateUpTo && (
          <Hint iconClassName="la la-info-circle" tooltip={t('common:job.rateFromHint', { companyName })} tooltipClassName={styles.hiddenRateTooltip} />
        )}
        <br />
        <small>
          ({convertMoneyRange(employment.rate, employment.rateUpTo, employment.currency, currency, employment.hideRateUpTo)}
          {' '}
          {t(`common:ratePeriod.${employment.ratePeriod}`)})
        </small>
        {renderRateExpectation(employment)}
      </>
    );
  };

  const favoritedAt = useSelector(reduxState => favoriteJobsSelectors.getFavoriteDate(reduxState, job ? job.id : null));
  const rateRange = job && job.monthlyRate ? formatRateRange(job.monthlyRate.czk, job.monthlyRateUpTo.czk, true, i18n.language, currency) : '';
  const rateRangeString = job && !/[0-9]{3,}/.test(job.title) ? `${rateRange} | ` : '';

  const applicationBlockers = [];
  // TODO: docasne vypnuto, zabiji job applications
  // const applicationBlockers = useMemo(() => {
  //   if (!(loggedIn && job)) {
  //     return [];
  //   }

  //   const blockers = [];

  //   if (loggedIn && job.minRequiredScore > job.score) {
  //     blockers.push({
  //       type: 'score',
  //       content: t('job.jobApplicationBlockers.lowScore', { score: job.score }),
  //     });
  //   }

  //   if (job.mustMeetSkills) {
  //     const requiredSkills = job.skills.filter(({ required }) => required);
  //     const requiredLangs = job.languages.filter(({ required }) => required);
  //     const hasAllRequireds = requiredSkills.every(({ slug, levelId }) => profileSkills[slug] && profileSkills[slug] >= levelId - 1)
  //       && requiredLangs.every(({ slug, levelId }) => profileLanguages[slug] && profileLanguages[slug] >= levelId - 1);

  //     if (!hasAllRequireds) {
  //       blockers.push({
  //         type: 'required',
  //         content: t('job.jobApplicationBlockers.requiredSkills'),
  //       });
  //     }
  //   }

  //   if (job.mustMeetLocality && job.locations.length > 0 && !willingToRelocate && job.remoteWork === REMOTE_WORK.NO) {
  //     if (profileLocations.length === 0) {
  //       blockers.push({
  //         type: 'locality',
  //         content: t('job.jobApplicationBlockers.locality')
  //       });
  //     }

  //     const meetsLocality = profileLocations.some((profileLocation) => {
  //       if (profileLocation.city && profileLocation.city.id) {
  //         const jobLocationsWithCity = job.locations
  //           .filter(jobLocation => !!(jobLocation.city && jobLocation.city.id));

  //         if (jobLocationsWithCity.length > 0) {
  //           return jobLocationsWithCity
  //             .some(jobLocation => jobLocation.city.id === profileLocation.city.id);
  //         }

  //         return job.locations.some(jobLocation => !jobLocation.city && jobLocation.country.id === profileLocation.country.id);
  //       }

  //       return job.locations
  //         .filter(jobLocation => !jobLocation.city)
  //         .some(jobLocation => jobLocation.country.id === profileLocation.country.id);
  //     });

  //     if (!meetsLocality) {
  //       blockers.push({
  //         type: 'locality',
  //         content: t('job.jobApplicationBlockers.locality'),
  //       });
  //     }
  //   }

  //   if (job.mustMeetEducation) {
  //     const unfulfilledEdu = job.educations.filter(edu => !!edu.required).some(edu => edu.level > highestEducationLevel);
  //     if (unfulfilledEdu) {
  //       blockers.push({
  //         type: 'education',
  //         content: t('job.jobApplicationBlockers.education'),
  //       });
  //     }
  //   }

  //   if (job.mustMeetExperience) {
  //     const unfulfilledExperience = job.experience.filter(edu => !!edu.required).some((exp) => {
  //       if (exp.roleId) {
  //         return exp.months > ~~rolesExperience[exp.roleId];
  //       }

  //       return exp.months > ~~fieldsExperience[exp.fieldId];
  //     });

  //     if (unfulfilledExperience) {
  //       blockers.push({
  //         type: 'experience',
  //         content: t('job.jobApplicationBlockers.experience')
  //       });
  //     }
  //   }

  //   return blockers;
  // }, [job, activeOffer, loggedIn]);

  useEffect(() => {
    if (applicationBlockers && applicationBlockers.length > 0) {
      applicationBlockers.forEach((applicationBlocker) => {
        googleAnalytics.event('job_application_blocker', {
          type: applicationBlocker.type,
          slug: job ? job.slug : undefined,
          userId,
          score: state.score || (job ? job.score : undefined),
        });
      });
    }
  }, [applicationBlockers]);

  // eslint-disable-next-line
  const onExpandActive = () => {
    googleAnalytics.event('expand_description_active', {
      slug: job.slug,
    });
  };

  const onExpandInactive = () => {
    googleAnalytics.event('expand_description_inactive', {
      slug: job.slug,
    });
  };

  const onSimilarJobClick = similarJobSlug => () => {
    googleAnalytics.event('similar_job_click', {
      from: job.slug,
      fromActive: job.active,
      to: similarJobSlug
    });
  };

  let helmetDescription = '';
  if (job && job.onelineSummary) {
    helmetDescription = job.onelineSummary;
  } else if (job && job.company) {
    helmetDescription = buildMetaDescription(`${job.company.name}. ${job.description}`);
  }

  const applicationNeedsAccount = false;
  // TODO: docasne vypnuto, zabiji job applications
  // const applicationNeedsAccount = useMemo(() => {
  //   if (!(job && job.educations && job.experience)) {
  //     return true;
  //   }

  //   const hasRequiredEdu = job.educations.filter(edu => !!edu.required).length > 0;
  //   const hasRequiredExp = job.experience.filter(edu => !!edu.required).length > 0;

  //   return !loggedIn && (job.minRequiredScore > 0 || job.mustMeetSkills || job.mustMeetLocality || hasRequiredEdu || hasRequiredExp);
  // }, [loggedIn, job]);

  return (
    <Page
      initialRow={false}
      helmetTitle={job && job.company ? buildSeoTitle(t, `${job.title} | ${rateRangeString}${job.company.name}`, false) : ''}
      helmetDescription={helmetDescription}
      seoUrl={job && job.slug ? createUserportalLink(routes.job.detail, { slug: job.slug }) : null}
      ogImage={job && job.ogImage ? createStaticsLink(`job-og/${job.ogImage}`) : null}
      subheader={state.errorCode === 404 && (
        <Subheader center title={t('page.job.notFound.title')}>
          <span>{t('page.job.notFound.subtitle')}</span>
        </Subheader>
      )}
    >
      <Loadable
        loading={state.loading || !job}
        canRender={!!job}
        error={state.error}
        render={() => {
          if (state.errorCode === 404) {
            return (
              <NotFound
                lines={[t('page.job.notFound.p1'), t('page.job.notFound.p2')]}
                ctaTitle={t('page.job.notFound.cta')}
                ctaRoute="/"
              />
            );
          }

          const hasIgnoredSkill = job.skills.some(skill => skill.id in profileIgnoredSkills);

          const missingSkills = job.scorePenalty
            ? [...(job.scorePenalty.coreSkills || []), ...(job.scorePenalty.importantSkills || [])].filter(skill => !skill.soft)
            : [];

          const scorePotentialTip = loggedIn && missingSkills.length > 0 && job.scorePenalty.baseScore - job.score >= 5 && (
            <Tip
              type="warning"
              iconClassName="none"
              className={styles.scorePotential}
              title={(
                <span className={styles.potentialTitle}>
                  <Trans
                    t={t}
                    i18nKey="page.job.scorePotential.title"
                    values={{ score: job.scorePenalty.baseScore }}
                    components={[<strong key="0" />]}
                  />
                </span>
              )}
            >
              <p className={styles.potentialSubtitle}>
                {t('page.job.scorePotential.missing', { count: missingSkills.length })}
              </p>
              <div>
                {missingSkills.map(skill => (
                  <Tag
                    isSmall
                    key={skill.slug}
                    type="skill"
                    slug={skill.slug}
                    wrapperClassName={styles.potentialSkill}
                    className={styles.potentialSkillTag}
                  >
                    {skill.title}
                  </Tag>
                ))}
              </div>
              <p className={styles.potentialHint}>
                {t('page.job.scorePotential.hint', { count: missingSkills.length })}
              </p>
            </Tip>
          );

          let countlyLink = 'https://countly.cz';
          try {
            const employmentPrice = Math.round(~~((job.employmentTypes.find(et => et.id === EMPLOYMENT.FULLTIME_EMPLOYMENT || et.id === EMPLOYMENT.PART_TIME_EMPLOYMENT) || {}).monthlyRateUpTo) * currencies[job.currency][DEFAULT_CURRENCY]);
            const contractPrice = Math.round(~~((job.employmentTypes.find(et => et.id === EMPLOYMENT.FULLTIME_CONTRACT || et.id === EMPLOYMENT.PART_TIME_CONTRACT) || {}).monthlyRateUpTo) * currencies[job.currency][DEFAULT_CURRENCY]);

            if (employmentPrice && contractPrice) {
              countlyLink = `https://countly.cz/hpp-vs-osvc-kalkulacka?hppIncome=${employmentPrice}&osvcIncome=${contractPrice}&osvcIncomeFrequency=za%20měsíc`;
            } else if (employmentPrice) {
              countlyLink = `https://countly.cz/hpp-kalkulacka?incomeBrutto=${employmentPrice}`;
            } else if (contractPrice) {
              countlyLink = `https://countly.cz/osvc-kalkulacka?income=${contractPrice}&incomeFrequency=za%20měsíc`;
            }
          } catch (err) {
            Sentry.captureException(err);
          }

          const aiTipsDisabledTooltip = hasInsufficientProfile ? t('common:profileNeeded') : null;

          const openAccountPage = (route, gaEvent) => {
            googleAnalytics.event(gaEvent, { slug: job.slug });
            setBackUrl({ pathname: createRoute(routes.job.detail, { slug: job.slug }) });
            navigate(route);
          };

          const jobSkills = [
            ...job.skills.filter(({ soft }) => !soft),
            ...job.skills.filter(({ soft }) => soft),
          ];

          const skillGroups = loggedIn ? _.groupBy(jobSkills.map((skill) => {
            const match = matcher.getSkillMatch(skill);
            return {
              ...skill,
              match: match === MATCH.NONE ? MATCH.LOW : match,
            };
          }), 'match') : {};
          const skillMatchGroups = MATCH_GROUPS.filter(group => group in skillGroups);

          const langGroups = loggedIn ? _.groupBy(job.languages.map((lang) => {
            const match = matcher.getLanguageMatch(lang);
            return {
              ...lang,
              match: match === MATCH.NONE ? MATCH.LOW : match,
            };
          }), 'match') : {};
          const langMatchGroups = MATCH_GROUPS.filter(group => group in langGroups);

          return (
            <>
              <div className={`row ${styles.header}`}>
                <div className="col-xl-8 col-lg-8 col-md-12 col-sm-12 col-xs-12">
                  <JobHeader
                    withRoles
                    withFavorite
                    className={styles.jobHeader}
                    job={job}
                    score={job.score}
                    matcher={matcher}
                  />
                </div>
                <div className={`col-xl-4 col-lg-4 col-md-12 col-sm-12 col-xs-12 ${styles.ctas}`}>
                  {(job.active || activeOffer || featureFlags.inactiveJobApplication) ? (
                    <JobApplicationButton
                      job={job}
                      onClick={applicationNeedsAccount ? toggleAccountRequiredModal : toggleApplicationFormOpened}
                      className={styles.applicationButton}
                      disabledReason={applicationBlockers.length > 0 ? t('job.jobApplicationDisabled') : null}
                    />
                  ) : (
                    <div className={styles.inactiveJob}>
                      <Tip inline type="warning" className="pr32 pl32">
                        {t('page.job.jobIsNotActive')}
                      </Tip>
                    </div>
                  )}
                </div>
              </div>
              {applicationFormOpened && (
                <JobApplicationForm
                  job={job}
                  loggedIn={loggedIn}
                  onClose={toggleApplicationFormOpened}
                  gaEvent="job_application_open_form"
                />
              )}
              <div className={cn('row', styles.contentRow)}>
                <div id="job-overview" className={cn('col-xl-8 col-lg-8 col-md-12 col-sm-12 col-xs-12', styles.leftBlock)}>
                  {applicationBlockers.length > 0 && (
                    <Tip type="warning" className={styles.lowScore}>
                      <h3 className="mb16">{t('job.jobApplicationDisabled')}</h3>
                      <ul className="pl16">
                        {applicationBlockers.map(blocker => (
                          <li key={blocker.type}>{blocker.content}</li>
                        ))}
                      </ul>
                      <p>
                        {t('job.jobApplicationBlockers.hint')}
                      </p>
                    </Tip>
                  )}
                  <Collapsable
                    enabled={!job.active && similarJobs.length > 0}
                    idToScrollOnCollapse="job-overview"
                    maxHeight={220}
                    collapseTitle={t('common:collapseDescription')}
                    expandTitle={t('common:expandDescription')}
                    className={styles.descriptionContent}
                    onExpand={onExpandInactive}
                  >
                    <div className={styles.description}>
                      {job.skills.length > 0 && (
                        <div className={styles.skills}>
                          <div className={styles.title}>
                            <h2>{t('common:job.requiredSkills')}</h2>
                            <LevelsHint />
                          </div>
                          {loggedIn ? (
                            <>
                              {skillGroups[MATCH.FULL] && (
                                <div className={styles.row}>
                                  <div className={styles.label}>
                                    <span className={cn(styles.match, styles.fullMatch)}>
                                      <i className="la la-check-circle" />
                                    </span>
                                    {t('match.title-full')}:
                                  </div>
                                  <div className={styles.skills}>
                                    {skillGroups[MATCH.FULL].map(skill => (
                                      <Tag
                                        key={skill.slug}
                                        component="li"
                                        slug={skill.slug}
                                        type="skill"
                                        subType={skill.soft ? 'soft' : null}
                                        level={skill.levelId}
                                        app="userportal"
                                        silentMatch
                                        isSmall
                                        match={matcher.getSkillMatch(skill)}
                                        isRequired={skill.required}
                                        ignored={skill.id in profileIgnoredSkills}
                                        linkUrl={!loggedIn ? getPathname([{ type: 'skill', slug: skill.slug }]) : null}
                                        wrapperClassName={styles.tagWrapper}
                                      >
                                        {skill.title}
                                      </Tag>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {skillMatchGroups.some(g => g !== MATCH.FULL) && (
                                <div className={styles.row}>
                                  <div className={styles.label}>
                                    <span className={cn(styles.match, styles.othersMatch)}>
                                      <i className="la la-question-circle" />
                                    </span>
                                    {t('match.title-others')}:
                                  </div>
                                  <div className={styles.skills}>
                                    {skillMatchGroups.filter(g => g !== MATCH.FULL).flatMap(g => skillGroups[g]).map(skill => (
                                      <Tag
                                        key={skill.slug}
                                        component="li"
                                        slug={skill.slug}
                                        type="skill"
                                        subType={skill.soft ? 'soft' : null}
                                        level={skill.levelId}
                                        app="userportal"
                                        silentMatch
                                        isSmall
                                        match={matcher.getSkillMatch(skill)}
                                        isRequired={skill.required}
                                        ignored={skill.id in profileIgnoredSkills}
                                        linkUrl={!loggedIn ? getPathname([{ type: 'skill', slug: skill.slug }]) : null}
                                        wrapperClassName={styles.tagWrapper}
                                      >
                                        {skill.title}
                                      </Tag>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <TagList className={styles.tags}>
                              {jobSkills.map(skill => (
                                <Tag
                                  isSmall
                                  key={skill.slug}
                                  component="li"
                                  slug={skill.slug}
                                  type="skill"
                                  subType={skill.soft ? 'soft' : null}
                                  level={skill.levelId}
                                  match={matcher.getSkillMatch(skill)}
                                  app="userportal"
                                  isRequired={skill.required}
                                  ignored={skill.id in profileIgnoredSkills}
                                  linkUrl={!loggedIn ? getPathname([{ type: 'skill', slug: skill.slug }]) : null}
                                  wrapperClassName={styles.tagWrapper}
                                >
                                  {skill.title}
                                </Tag>
                              ))}
                            </TagList>
                          )}
                        </div>
                      )}

                      {job.languages.length > 0 && (
                        <div className={cn(styles.skills, 'mt8')}>
                          <div className={styles.title}>
                            <h2>{t('common:job.languages')}</h2>
                            <LevelsHint />
                          </div>
                          {loggedIn ? (
                            <>
                              {langGroups[MATCH.FULL] && (
                                <div className={styles.row}>
                                  <div className={styles.label}>
                                    <span className={cn(styles.match, styles.fullMatch)}>
                                      <i className="la la-check-circle" />
                                    </span>
                                    {t('match.title-full')}:
                                  </div>
                                  <div className={styles.skills}>
                                    {langGroups[MATCH.FULL].map(lang => (
                                      <Tag
                                        key={lang.languageId}
                                        component="li"
                                        slug={lang.slug}
                                        type="language"
                                        level={lang.levelId}
                                        silentMatch
                                        isSmall
                                        match={matcher.getLanguageMatch(lang)}
                                        app="userportal"
                                        isRequired={lang.required}
                                        wrapperClassName={styles.tagWrapper}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                              {langMatchGroups.some(g => g !== MATCH.FULL) && (
                                <div className={styles.row}>
                                  <div className={styles.label}>
                                    <span className={cn(styles.match, styles.othersMatch)}>
                                      <i className="la la-question-circle" />
                                    </span>
                                    {t('match.title-others')}:
                                  </div>
                                  <div className={styles.skills}>
                                    {langMatchGroups.filter(g => g !== MATCH.FULL).flatMap(g => langGroups[g]).map(lang => (
                                      <Tag
                                        key={lang.languageId}
                                        component="li"
                                        slug={lang.slug}
                                        type="language"
                                        level={lang.levelId}
                                        silentMatch
                                        isSmall
                                        match={matcher.getLanguageMatch(lang)}
                                        app="userportal"
                                        isRequired={lang.required}
                                        wrapperClassName={styles.tagWrapper}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <TagList className={styles.tags}>
                              {job.languages.map(lang => (
                                <Tag
                                  isSmall
                                  key={lang.languageId}
                                  component="li"
                                  slug={lang.slug}
                                  type="language"
                                  level={lang.levelId}
                                  match={matcher.getLanguageMatch(lang)}
                                  app="userportal"
                                  isRequired={lang.required}
                                  wrapperClassName={styles.tagWrapper}
                                />
                              ))}
                            </TagList>
                          )}
                        </div>
                      )}

                      {/* EDU demo promos */}
                      <EduPromos job={job} />

                      <div className={styles.reviewPromo}>
                        <div className={styles.reviewPromoHead}>
                          <h2>{t('page.job.userJobReview.promoTitle')}</h2>
                          <span className={styles.reviewPromoBadge}>
                            {t('page.job.userJobReview.promoBadge')}
                          </span>
                        </div>
                        <p className={styles.reviewPromoText}>
                          {t('page.job.userJobReview.promoText')}
                        </p>
                        <ul className={styles.reviewPromoList}>
                          <li>
                            <i className="la la-thumbs-up" />
                            {t('page.job.userJobReview.promoItem1')}
                          </li>
                          <li>
                            <i className="la la-eye" />
                            {t('page.job.userJobReview.promoItem2')}
                          </li>
                          <li >
                            <i className="la la-compass" />
                            {t('page.job.userJobReview.promoItem3')}
                          </li>
                          <li>
                            <i className="la la-exclamation-circle" />
                            {t('page.job.userJobReview.promoItem4')}
                          </li>
                        </ul>
                        {loggedIn ? (
                          <ButtonWithModal
                            fullHeightModal
                            noHeader
                            noBodyPadding
                            modalSize="xl"
                            buttonComponent={aiTipsDisabledTooltip ? (
                              <Tooltip content={aiTipsDisabledTooltip}>
                                <Submit
                                  disabled
                                  noMargin
                                  title={t('page.job.userJobReview.modalButton')}
                                />
                              </Tooltip>
                            ) : (
                              <Submit
                                noMargin
                                title={t('page.job.userJobReview.modalButton')}
                              />
                            )}
                            render={({ closeModal }) => (
                              <UserJobReviewModal
                                jobId={job.id}
                                score={job.score}
                                scoreDims={job.scoreDims}
                                onClose={closeModal}
                              />
                            )}
                          />
                        ) : (
                          <div className={styles.reviewPromoLogin}>
                            <p>{t('page.job.userJobReview.loginPrompt')}</p>
                            <div className={styles.reviewPromoLoginButtons}>
                              <Submit
                                noMargin
                                iconClassName="la la-sign-in-alt"
                                title={t('page.job.userJobReview.login')}
                                onClick={() => openAccountPage(routes.loginAsUser, 'user_ai_review_login')}
                              />
                              <Button
                                iconClassName="la la-user-plus"
                                title={t('page.job.userJobReview.signup')}
                                onClick={() => openAccountPage(routes.signupAsUser, 'user_ai_review_signup')}
                              />
                            </div>
                          </div>
                        )}
                      </div>


                      {/* <h2 id="job-description" className="mt8">
                        {t('common:job.description')}
                      </h2> */}
                      {/* <Collapsable
                        enabled={job.active}
                        idToScrollOnCollapse="job-overview"
                        maxHeight={job.benefits.length > 0 ? 200 : 500}
                        collapseTitle={t('common:collapseDescription')}
                        expandTitle={t('common:expandDescription')}
                        className={styles.descriptionContent}
                        onExpand={onExpandActive}
                      > */}
                      <div className={styles.descriptionContent}>
                        <WysiwygContent>{job.description}</WysiwygContent>
                      </div>
                      {/* </Collapsable> */}

                      {job.benefits.length > 0 && (
                        <>
                          <h2>{t('page.job.benefits')}</h2>
                          <div className="row">
                            {job.benefits.map(benefit => (
                              <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6 col-xl-4" key={benefit.id}>
                                <JobAttribute
                                  noLeftSpace
                                  iconClassName="la la-cube"
                                  title={benefit.title}
                                >
                                  {benefit.description || t('common:yes')}
                                </JobAttribute>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </Collapsable>
                </div>
                <div className={cn('col-xl-4 col-lg-4 col-md-12 col-sm-12 col-xs-12 mb16', styles.rightBlock)}>
                  <h2 className={styles.overviewTitle}>{t('page.job.infoTitle')}</h2>
                  <div className={styles.scoreCard}>
                    <div className={styles.scoreRow}>
                      <div className={styles.scoreCircle}>
                        <Score
                          id={job.id}
                          score={job.score}
                          type="your"
                          inactiveJob={job.active === false}
                          ignored={hasIgnoredSkill}
                        />
                      </div>
                      <div className={styles.scoreInfo}>
                        <h4>{t('common:scoreName')}</h4>
                        {loggedIn ? (
                          job.active !== false
                            ? <span>{t('common:score.your', { scoreName: t('common:scoreName'), score: job.score })}</span>
                            : <span>{t('userportal:page.job.jobIsNotActive')}</span>
                        ) : (
                          <span>
                            <Link
                              to={{ pathname: routes.login }}
                              onClick={() => setBackUrl({ pathname: createRoute(routes.job.detail, { slug: job.slug }) })}
                            >
                              {t('loginToDisplay')}
                            </Link>
                          </span>
                        )}
                      </div>
                    </div>
                    {scorePotentialTip}
                    {loggedIn && job.scoreDims && (
                      <ScoreDims
                        dims={job.scoreDims}
                        showValue
                        stacked
                        tooltipOnIcon
                        className={styles.dimensions}
                        getLabel={key => t(`common:score.dim.${key}`)}
                        getTooltip={(key, value) => (
                          <>
                            <strong>{t(`common:score.dim.${key}`)}: {value}%</strong>
                            <br />
                            {t(`common:score.dimTooltip.${key}`, { context: user?.gender })}
                          </>
                        )}
                      />
                    )}
                  </div>
                  <ul className={styles.jobAttributes}>
                    <li className={styles.jobAttribute}>
                      <div className={styles.attrIcon}><i className="la la-file-contract" /></div>
                      <div className={styles.attrContent}>
                        <h4>{t('common:job.employmentType')}</h4>
                        {job.employmentTypes.map(employment => (
                          <div key={employment.id} className={styles.employment}>
                            <span>{t(`common:employment.${employment.id}`)}</span>
                            <span>
                              {formatRate(job.company.name, employment)}
                              {employment.minAllocation && <>, {t('common:job.minAllocation')}: {employment.minAllocation} {t('common:unit.hoursPerWeekPlural', { postProcess: 'interval', count: employment.minAllocation })}</>}
                            </span>
                          </div>
                        ))}
                        <div className={cn(styles.employment, styles.countly)}>
                          <span>
                            {t('page.job.countlyTitle')}
                          </span>
                          <span>
                            {t('page.job.countlyText')} <a href={countlyLink} target="_blank" rel="noopener noreferrer">Countly.cz</a>
                          </span>
                        </div>
                      </div>
                    </li>
                    {job.educations.length > 0 && (
                      <li className={styles.jobAttribute}>
                        <div className={styles.attrIcon}><i className="la la-graduation-cap" /></div>
                        <div className={styles.attrContent}>
                          <h4>{t('common:job.education')}</h4>
                          <span className={styles.links}>
                            {t(`common:schoolType.${job.educations[0].id}`)}
                            {job.educations[0].required && ` (${t('common:form.isRequired')})`}
                          </span>
                        </div>
                      </li>
                    )}
                    {job.experience.length > 0 && (
                      <li className={styles.jobAttribute}>
                        <div className={styles.attrIcon}><i className="la la-user-tie" /></div>
                        <div className={styles.attrContent}>
                          <h4>{t('common:job.experience')}</h4>
                          <span className={styles.links}>
                            {job.experience.map((exp) => {
                              const years = Math.round(exp.months / 12);
                              return (
                                <span key={exp.id}>
                                  {years} {t('common:experience.years', { postProcess: 'interval', count: years })}
                                  {' '}
                                  {t('common:job.expInField')} <strong>{t(`common:field.${exp.fieldSlug}`)}</strong>
                                  {exp.roleSlug && (
                                    <>{' '}{t('common:job.expInFieldRole')} <strong>{t(`common:role:${exp.roleSlug}`)}</strong></>
                                  )}
                                  {exp.required && ` (${t('common:form.isRequired')})`}
                                </span>
                              );
                            })}
                          </span>
                        </div>
                      </li>
                    )}
                    {job.suitableFor.length > 0 && (
                      <li className={styles.jobAttribute}>
                        <div className={styles.attrIcon}><i className="la la-user-tie" /></div>
                        <div className={styles.attrContent}>
                          <h4>{t('common:job.suitableForTitle')}</h4>
                          <span className={styles.links}>
                            {job.suitableFor.map((suitableFor, i) => (
                              <Fragment key={suitableFor}>
                                {i > 0 ? ', ' : null}
                                <Link to={getPathname([{ type: 'suitableFor', slug: suitableFor }])}>
                                  {t(`common:job.suitableFor.${suitableFor}`)}
                                </Link>
                              </Fragment>
                            ))}
                          </span>
                        </div>
                      </li>
                    )}
                    <li className={styles.jobAttribute}>
                      <div className={styles.attrIcon}><i className="la la-calendar" /></div>
                      <div className={styles.attrContent}>
                        <h4>{t('common:job.allocation')}</h4>
                        {(job.allocationDuration && job.allocationPeriod) ? (
                          <span>
                            {job.allocationDuration} {t(`common:allocationPeriod.${job.allocationPeriod}`, { postProcess: 'interval', count: job.allocationDuration })},
                            {' '}
                            {job.allocationExtendable ? t('common:job.extendable') : t('common:job.notExtendable')}
                          </span>
                        ) : (
                          <span>{t('common:job.allocationUnlimited')}</span>
                        )}
                      </div>
                    </li>
                    <li className={styles.jobAttribute}>
                      <div className={styles.attrIcon}><i className="la la-map-marked-alt" /></div>
                      <div className={styles.attrContent}>
                        <h4>{t('common:job.locality')}</h4>
                        {job.locations.length > 0
                          ? job.locations.map(loc => (
                            <span key={loc.id} className={styles.links}>
                              {loc.country
                                ? (loc.city
                                  ? <>
                                    <Link to={getPathname([{ type: 'city', slug: loc.city.slug }])}>{loc.city.title}</Link>
                                    {' '}
                                    (<Link to={getPathname([{ type: 'country', slug: loc.country.slug }])}>{loc.country.title}</Link>)
                                    </>
                                  : <Link to={getPathname([{ type: 'country', slug: loc.country.slug }])}>{loc.country.title}</Link>)
                                : t('common:job.anyLocality')}
                            </span>
                          ))
                          : (<span>{t('common:notSpecified')}</span>)
                        }
                      </div>
                    </li>
                    <li className={styles.jobAttribute}>
                      <div className={styles.attrIcon}><i className="la la-home" /></div>
                      <div className={styles.attrContent}>
                        <h4>{t('common:job.homeofficeTitle')}</h4>
                        <span>{t(`common:job.homeoffice.${job.homeoffice}`)}</span>
                      </div>
                    </li>
                    <li className={styles.jobAttribute}>
                      <div className={styles.attrIcon}><i className="la la-globe" /></div>
                      <div className={styles.attrContent}>
                        <h4>{t('common:job.remoteWorkTitle')}</h4>
                        <span>{t(`common:job.remoteWork.${job.remoteWork}`)}</span>
                      </div>
                    </li>
                    <li className={styles.jobAttribute}>
                      <div className={styles.attrIcon}><i className={job.company.agency ? 'la la-user-tie' : 'la la-building'} /></div>
                      <div className={styles.attrContent}>
                        <h4>{t('common:form.companyType')}</h4>
                        <span>{job.company.agency ? t('common:companyType.agency') : t('common:companyType.employer')}</span>
                      </div>
                    </li>
                    {!job.company.agency && (
                      <li className={styles.jobAttribute}>
                        <div className={styles.attrIcon}><i className="la la-users" /></div>
                        <div className={styles.attrContent}>
                          <h4>{t('page.company.size')}</h4>
                          <span>{job.company.size ? t(`common:companySizeDetail.${job.company.size}`) : t('common:notSpecified')}</span>
                        </div>
                      </li>
                    )}
                    {job.publishedAt && (
                      <li className={styles.jobAttribute}>
                        <div className={styles.attrIcon}><i className="la la-calendar" /></div>
                        <div className={styles.attrContent}>
                          <h4>{t('common:job.publishedAt')}</h4>
                          <span>{formatDateTime(job.publishedAt, i18n.language)}</span>
                        </div>
                      </li>
                    )}
                    {!job.active && job.deactivatedAt && (
                      <li className={styles.jobAttribute}>
                        <div className={styles.attrIcon}><i className="la la-calendar" /></div>
                        <div className={styles.attrContent}>
                          <h4>{t('common:job.deactivatedAt')}</h4>
                          <span>{formatDateTime(job.deactivatedAt, i18n.language)}</span>
                        </div>
                      </li>
                    )}
                    {favoritedAt && (
                      <li className={styles.jobAttribute}>
                        <div className={styles.attrIcon}><i className="lar la-heart" /></div>
                        <div className={styles.attrContent}>
                          <h4>{t('common:job.favoritedAt')}</h4>
                          <span>{formatDateTime(favoritedAt)}</span>
                        </div>
                      </li>
                    )}
                  </ul>
                </div>
                {similarJobs.length > 0 && (
                  <div className={cn('col-xl-8 col-lg-8 col-md-12 col-sm-12 col-xs-12', styles.leftBlock)}>
                    {!job.active && (
                      <Tip type="warning" className="mb16">
                        <h3>{t('page.job.inactivaAlertTitle')}</h3>
                        <p>
                          {t('page.job.inactivaAlertText')}
                        </p>
                      </Tip>
                    )}
                    <div>
                      <h2 className={styles.similarJobs}>{t('page.job.similarJobs')}</h2>
                      <CardList>
                        {similarJobs.map(similarJob => (
                          <Card key={similarJob.slug} noBorder spacingBottom="small">
                            <JobPreview
                              // noScore={!loggedIn}
                              withSmallTags
                              withSeoLinks
                              loggedIn={loggedIn}
                              job={similarJob}
                              currency={currency}
                              onClick={onSimilarJobClick(similarJob.slug)}
                              // searchItems={searchItems}
                              // onRoleClick={onRoleClick}
                              // onSkillClick={onSkillClick}
                              // onEmploymentTypeClick={onEmploymentTypeClick}
                              // onCompanySizeClick={onCompanySizeClick}
                              // onCountryClick={onCountryClick}
                              // onCityClick={onCityClick}
                              // onBenefitClick={onBenefitClick}
                              // onRateClick={onRateClick}
                            />
                          </Card>
                        ))}
                      </CardList>
                    </div>
                  </div>
                )}
                <div className="clear" />
              </div>
              <span dangerouslySetInnerHTML={{ __html: createSchemaOrg(job) }} />
            </>
          );
        }}
      />
      <AccountRequiredModal isOpen={accountRequiredModalOpened} toggle={toggleAccountRequiredModal} />
    </Page>
  );
};

export default JobDetail;
